/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * LIVE PROGRESS (item 231). Shown from the moment a program is handed over —
 * sent, active, scoring, completed — so "track progress as it happens" is a
 * thing the app does rather than a thing the marketing page says. Numbers come
 * from the tenant's depletion reports through the resolver; goals that have no
 * system source say so in their own words instead of showing a fake zero.
 *
 * This never writes. Scores are captured on the scoring screen, deliberately by
 * a human, and once captured they are what this panel reports.
 */
import { useEffect, useState } from "react";
import { programProgress, barWidth } from "../utils/progress.js";
import { num } from "../utils/payout.js";
import { METRIC_LABEL } from "../utils/constants.js";
import GoalScope from "./GoalScope.jsx";

function money(n) { return `$${num(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`; }

export function GoalProgressRow({ item }) {
  const { goal } = item;
  const unit = goal.unit_label ? ` ${goal.unit_label}` : "";
  const target = num(goal.target_value);

  return (
    <div className="it-prog-row">
      <div className="it-prog-head">
        <strong>{goal.description || METRIC_LABEL[goal.metric_type] || "Goal"}</strong>
        {item.hasActual && target > 0 && (
          <span className={`it-prog-pct ${item.pct >= 100 ? "met" : ""}`}>{item.pct.toFixed(0)}%</span>
        )}
      </div>

      {item.hasActual ? (
        <>
          {target > 0 && (
            <div className="it-prog-bar" role="img"
                 aria-label={`${item.pct.toFixed(0)} percent of target`}>
              <span className={item.pct >= 100 ? "met" : ""} style={{ width: barWidth(item.pct) }} />
            </div>
          )}
          <div className="it-prog-meta">
            <span>
              <strong>{num(item.actual).toLocaleString()}{unit}</strong>
              {target > 0 ? ` of ${target.toLocaleString()}${unit}` : ""}
            </span>
            {item.payout != null && <span>{money(item.payout)} {item.scored ? "earned" : "on track"}</span>}
          </div>
          <p className="it-source">{item.basis}</p>
        </>
      ) : (
        <p className="it-source">{item.reason}</p>
      )}

      <GoalScope goal={goal} compact />
    </div>
  );
}

export default function ProgressPanel({ program, goals, scores, accountId, heading = "Progress" }) {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let alive = true;
    // Wait for the tenant to resolve — firing early would report "source
    // unavailable" on every goal and then flicker to the real answer.
    if (!accountId) return () => { alive = false; };
    setState({ loading: true, data: null });
    programProgress({ program, goals, scores, accountId })
      .then((d) => { if (alive) setState({ loading: false, data: d }); })
      .catch((e) => {
        console.error("[incentive.ProgressPanel]", e);
        if (alive) setState({ loading: false, data: null });
      });
    return () => { alive = false; };
    // Re-resolve when the program or its goals/scores change identity.
  }, [program, goals, scores, accountId]);

  if (goals.length === 0) return null;
  if (state.loading) return <p className="muted">Working out progress…</p>;
  if (!state.data) return <p className="muted">Progress couldn&apos;t be worked out just now.</p>;

  const { items, pct, goalsMet, targetedCount, payout, measuredCount, allScored } = state.data;

  return (
    <div className="it-progress">
      <div className="section-row" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>{heading}</h2>
        <span className="caption">
          {measuredCount === 0
            ? "Nothing measurable yet"
            : allScored
              ? `Final · ${goalsMet} of ${targetedCount} goals met`
              : `${goalsMet} of ${targetedCount} goals met · ${pct.toFixed(0)}% average attainment`}
        </span>
      </div>

      {payout > 0 && (
        <div className="it-prog-total">
          <span>{allScored ? "Total payout earned" : "Payout on current pace"}</span>
          <strong>{money(payout)}</strong>
        </div>
      )}

      {items.map((item) => <GoalProgressRow key={item.goal.id} item={item} />)}
    </div>
  );
}
